import React, { useEffect, useState, useMemo } from "react";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { toast } from "sonner";
import { Loader2, ChevronDown, ChevronRight, TrendingUp, TrendingDown, DollarSign, Timer, Wallet, Download, FileSpreadsheet, FileText as FileTextIcon, Users as UsersIcon, Building2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CentralizatorExport } from "@/pages/employee/reports/CentralizatorExport";

const RO_MONTHS = ["Ianuarie","Februarie","Martie","Aprilie","Mai","Iunie","Iulie","August","Septembrie","Octombrie","Noiembrie","Decembrie"];
function monthKey(d){return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;}
function fmt(n) { return (n||0).toLocaleString("ro-RO", { maximumFractionDigits: 0 }); }
function fmtHrs(n) { return `${(n||0).toFixed(1)} h`; }

export default function Reports() {
  const [cursor, setCursor] = useState(() => new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);

  const monthStr = monthKey(cursor);
  const y = cursor.getFullYear(), m = cursor.getMonth();

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/staff/reports/summary", { params: { month: monthStr } });
      setData(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [monthStr]);

  const totals = data?.totals || { hours: 0, cost: 0, revenue: 0, total_cost: 0, margin: 0, margin_pct: 0, project_count: 0 };
  const projects = data?.projects || [];

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1400px]">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <div className="text-[13px] font-display font-medium text-ink-muted mb-3">— P&amp;L pe proiect</div>
            <h1 className="font-display font-semibold text-[42px] md:text-[48px] tracking-[-0.02em] leading-tight text-ink">Rapoarte financiare</h1>
            <p className="mt-3 text-ink-muted">Buget vs actual, cost intern, venit către client și marjă.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setCursor(new Date(y, m-1, 1))} className="h-11 px-4 border border-neutral-200 hover:border-brand text-sm rounded-lg">← Precedent</button>
            <div className="h-11 px-6 border border-neutral-200 flex items-center font-display font-bold min-w-[220px] justify-center rounded-lg">{RO_MONTHS[m]} {y}</div>
            <button onClick={() => setCursor(new Date(y, m+1, 1))} className="h-11 px-4 border border-neutral-200 hover:border-brand text-sm rounded-lg">Următor →</button>
            <Button variant="primary" size="md" icon={Download}
              onClick={() => setExportOpen(true)} data-testid="btn-export">
              Export rapoarte
            </Button>
          </div>
        </div>

        <ExportDialog open={exportOpen} onClose={() => setExportOpen(false)} month={monthStr} />

        {loading ? (
          <div className="flex items-center justify-center py-24 text-ink-muted"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Se încarcă...</div>
        ) : (
          <>
            {/* Totals cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
              <StatCard icon={Timer} label="Ore lucrate" value={fmtHrs(totals.hours)} />
              <StatCard icon={Wallet} label="Cost intern" value={`€${fmt(totals.cost)}`} />
              <StatCard icon={DollarSign} label="Venit brut" value={`€${fmt(totals.revenue)}`} accent />
              <StatCard icon={Wallet} label="Cost total (dir+ind)" value={`€${fmt(totals.total_cost)}`} />
              <StatCard icon={totals.margin >= 0 ? TrendingUp : TrendingDown} label="Marjă" value={`€${fmt(totals.margin)} · ${totals.margin_pct.toFixed(1)}%`} good={totals.margin >= 0} />
            </div>

            {/* Projects table */}
            <div className="bg-white border border-neutral-200" data-testid="reports-table">
              <div className="grid grid-cols-[1fr_100px_100px_120px_120px_120px_120px_120px_60px] px-4 py-3 border-b border-neutral-200 bg-ink-soft font-mono text-[10px] uppercase tracking-widest text-ink-muted">
                <div>Proiect</div>
                <div className="text-right">Ore</div>
                <div className="text-right">Buget h</div>
                <div className="text-right">Cost intern</div>
                <div className="text-right">Chelt. dir.</div>
                <div className="text-right">Cost total</div>
                <div className="text-right">Venit</div>
                <div className="text-right">Marjă</div>
                <div></div>
              </div>
              {projects.length === 0 && (
                <div className="p-8 text-center text-ink-muted">Nicio activitate în această lună.</div>
              )}
              {projects.map((p) => {
                const isOpen = expanded === p.project_id;
                const good = p.margin >= 0;
                return (
                  <div key={p.project_id} className="border-b border-neutral-100 last:border-b-0">
                    <button onClick={() => setExpanded(isOpen ? null : p.project_id)}
                      data-testid={`report-row-${p.project_code}`}
                      className="w-full grid grid-cols-[1fr_100px_100px_120px_120px_120px_120px_120px_60px] px-4 py-3 hover:bg-neutral-50 text-left text-sm items-center">
                      <div>
                        <div className="font-mono text-[10px] text-ink-muted">{p.project_code}</div>
                        <div className="font-medium">{p.project_name}</div>
                      </div>
                      <div className="text-right font-mono">{fmtHrs(p.hours)}</div>
                      <div className="text-right font-mono text-ink-muted">{p.budget_hours || "—"}</div>
                      <div className="text-right font-mono">€{fmt(p.cost)}</div>
                      <div className="text-right font-mono">€{fmt(p.direct_expenses)}</div>
                      <div className="text-right font-mono">€{fmt(p.total_cost)}</div>
                      <div className="text-right font-mono font-semibold text-brand">€{fmt(p.revenue)}</div>
                      <div className={`text-right font-mono font-bold ${good ? "text-emerald-600" : "text-red-600"}`}>
                        €{fmt(p.margin)} <span className="text-[10px]">({p.margin_pct.toFixed(0)}%)</span>
                      </div>
                      <div className="text-right">{isOpen ? <ChevronDown className="h-4 w-4 inline text-brand" /> : <ChevronRight className="h-4 w-4 inline text-ink-muted" />}</div>
                    </button>
                    {isOpen && (
                      <div className="bg-neutral-50 px-4 py-4 border-t border-neutral-100">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-2">Detaliere pe angajat</div>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-ink-muted text-xs">
                              <th className="pb-1 font-mono">Angajat</th>
                              <th className="pb-1 font-mono text-right">Ore</th>
                              <th className="pb-1 font-mono text-right">Rate</th>
                              <th className="pb-1 font-mono text-right">Cost/h</th>
                              <th className="pb-1 font-mono text-right">Cost total</th>
                              <th className="pb-1 font-mono text-right">Venit</th>
                            </tr>
                          </thead>
                          <tbody>
                            {p.by_employee.map((e) => (
                              <tr key={e.employee_id} className="border-t border-neutral-200">
                                <td className="py-1.5">
                                  <div className="font-medium">{e.employee_name}</div>
                                  <div className="text-[10px] text-ink-muted">{e.job_title}</div>
                                </td>
                                <td className="py-1.5 text-right font-mono">{fmtHrs(e.hours)}</td>
                                <td className="py-1.5 text-right font-mono">€{fmt(e.fee_rate)}</td>
                                <td className="py-1.5 text-right font-mono">€{fmt(e.internal_rate)}</td>
                                <td className="py-1.5 text-right font-mono">€{fmt(e.cost)}</td>
                                <td className="py-1.5 text-right font-mono text-brand font-semibold">€{fmt(e.revenue)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </PortalLayout>
  );
}

function StatCard({ icon: Icon, label, value, accent, good }) {
  return (
    <div className={`p-5 border ${accent ? "border-brand bg-brand/5" : "border-neutral-200 bg-white"}`}>
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{label}</div>
        <Icon className={`h-4 w-4 ${good === false ? "text-red-600" : good ? "text-emerald-600" : accent ? "text-brand" : "text-ink-muted"}`} />
      </div>
      <div className={`mt-2 font-display font-black text-2xl tracking-tight ${accent ? "text-brand" : "text-ink"}`}>{value}</div>
    </div>
  );
}


// ==========================================================================
// EXPORT DIALOG — CSV & XLSX pentru 3 tipuri de rapoarte
// ==========================================================================
function ExportDialog({ open, onClose, month }) {
  const [busy, setBusy] = useState(null);
  const [selMonth, setSelMonth] = useState(month);

  useEffect(() => { if (open) setSelMonth(month); }, [open, month]);

  async function download(endpoint, format, filenameSlug) {
    setBusy(`${endpoint}-${format}`);
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const url = `${backend}/api/staff/reports/export/${endpoint}?month=${selMonth}&format=${format}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${filenameSlug}-${selMonth}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success(`Export ${format.toUpperCase()} descărcat`);
    } catch (e) {
      toast.error(`Export eșuat: ${e.message || e}`);
    } finally { setBusy(null); }
  }

  const REPORTS = [
    {
      key: "timesheet-detail", filename: "pontaj-detaliat",
      icon: FileTextIcon,
      title: "Pontaj detaliat",
      description: "Fiecare intrare de pontaj — data, angajat, proiect, ore, descriere, status.",
    },
    {
      key: "expenses", filename: "deconturi",
      icon: FileTextIcon,
      title: "Deconturi",
      description: "Cheltuielile din deconturi din luna selectată — angajat, proiect, categorie, sumă, sumă aprobată și status. Excelul include filtre pe antet.",
    },
    {
      key: "allocations-simple", filename: "alocari-ore-per-proiect",
      icon: UsersIcon,
      title: "Alocări — ore per proiect",
      description: "Doar angajat, cod proiect, nume proiect și totalul de ore alocate pe fiecare proiect.",
    },
    {
      key: "salaries", filename: "salarii",
      icon: FileSpreadsheet,
      title: "Salarii — alocare pe proiecte",
      description: "Model Template salarii: salariul fiecărui angajat repartizat pe proiecte proporțional cu orele pontate. Include Cod SAGA, salariu BRUT, clauză mobilitate, salariu total (BRUT×1.0225 + mobilitate), procent și salariu alocat.",
    },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[min(720px,95vw)] w-full">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Export rapoarte</DialogTitle>
          <DialogDescription>
            Alege luna, apoi descarcă rapoartele în format CSV sau Excel (XLSX).
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 flex items-end gap-3">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted block mb-1">Luna</label>
            <input type="month" value={selMonth} onChange={e => setSelMonth(e.target.value)}
              data-testid="export-month" className="border border-neutral-300 px-3 py-2 text-sm bg-white" />
          </div>
        </div>
        <div className="mt-4 space-y-3">
          <CentralizatorExport month={selMonth} />
          {REPORTS.map((r) => (
            <div key={r.key} className="border border-neutral-200 rounded-xl p-5">
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-lg flex items-center justify-center bg-neutral-100 text-ink-muted shrink-0">
                  <r.icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[15px] font-medium text-ink">{r.title}</div>
                  <div className="text-[13px] text-ink-muted mt-0.5">{r.description}</div>
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <Button variant="secondary" size="sm" icon={FileTextIcon}
                      loading={busy === `${r.key}-csv`}
                      onClick={() => download(r.key, "csv", r.filename)}>
                      Descarcă CSV
                    </Button>
                    <Button variant="primary" size="sm" icon={FileSpreadsheet}
                      loading={busy === `${r.key}-xlsx`}
                      onClick={() => download(r.key, "xlsx", r.filename)}>
                      Descarcă Excel
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="text-[12px] text-ink-muted mt-4">
          Rapoartele sunt generate pentru luna <span className="font-mono">{selMonth}</span>.
        </div>
      </DialogContent>
    </Dialog>
  );
}

